/**
 * crmLeadNextStep — pure rule engine porting the legacy lead "next step" guidance.
 *
 * Given a normalized lead + its interactions (newest first) + its judgment-layer row (from
 * services/leadJudgment.js#getLeadJudgment, null for the common no-override case — see
 * docs/CRM.md's 2026-07 "Lead judgment layer" entries for the 5-axis background) + its
 * qualification-checklist row (from services/leadQualification.js#getLeadQualification, null if
 * not yet started — a human-confirmed "the qualifying conversation actually happened" signal,
 * since interaction-type heuristics alone can't tell a real conversation with the decision-maker
 * from an inconclusive call with an intermediary), returns the single recommended next action:
 * { step, title, desc, urgency: 'red'|'amber'|'teal'|'gray', action: 'log'|'decision'|'convert'|
 * 'requalify'|null, prefill?, skip? }. No React, no I/O — easy to reason about/test.
 *
 * At the Lead stage the only job is deciding whether this is a serious opportunity worth
 * pursuing — gathering a formal project brief (the "Lead Intake" instrument) happens AFTER
 * conversion, not before. A prior "Send the project intake" step here read as confusing overlap
 * with the marketing guide and pre-empted work that belongs to the Opportunity stage — removed
 * 2026-07 (Matt). The underlying Lead Intake feature (services/leadIntake.js, api/lead-intake.js,
 * the public /intake page, and its Supabase tables) is left in place, unused for now, in case a
 * similar post-conversion intake flow is wanted later.
 *
 * Judgment axes and where they plug in:
 *  - Axis 4 (type mismatch) short-circuits everything below it — a `not_a_client_type` lead never
 *    reaches acknowledge/intake/qualify/decision/cold-fallback at all.
 *  - Axis 2 (slow burn) intercepts after rule 1 (a slow-burn lead still needs its real first
 *    response) and before the intake gate, running its own long-horizon cadence instead of the
 *    standard funnel. It also widens the intakeSkipped gate.
 *  - Axis 5 (reactivation) filters which interactions count as "human" so a freshly-reactivated
 *    lead doesn't inherit pre-disqualification context as if it were still current.
 *  - Axis 1 (relationship sensitivity) and axis 3 (fit rating) are pure post-processing over
 *    whatever the rules above already produced — see `finalize()` — so they apply uniformly
 *    without threading through every branch.
 */
import { LOW_FIT } from '../../services/leadJudgment.js'
import { QUALIFICATION_QUESTIONS } from '../../services/leadQualification.js'

// Lead Activity Types that are auto-logged (don't count as a human touch).
const AUTO_TYPES = ['Inbound Inquiry', 'Referral Received', 'Converted to Prospect']

const NOT_CLIENT_LABELS = {
  trade_or_referral_partner: 'Trade/referral partner',
  vendor_or_consultant: 'Vendor or consultant',
  other: 'Other',
}

// Softer, socially-framed copy for a relationship-sensitive lead — replaces the standard
// step's title/desc where it exists; steps not listed keep their normal copy (just with red
// downgraded to amber by dampenForRelationship, below).
const RELATIONSHIP_SOFTEN = {
  acknowledge: { title: 'Reach out when it feels natural', desc: 'This is a personal/relationship lead — a warm, low-pressure check-in fits better than a standard acknowledgment clock.' },
  'qualify-call': { title: 'Find a natural moment to talk specifics', desc: 'Confirm scope, site, timeline, and budget fit when the relationship allows — no need to force it.' },
  decision: { title: 'Make a qualification decision, on your own timeline', desc: 'The usual conversation has happened. Decide when it makes sense for this relationship.' },
  overdue: { title: 'Check in, relationship-style', desc: "It's been a while — a low-pressure personal check-in fits better than treating this as cold." },
  momentum: { title: 'Keep a light touch going', desc: 'No rush — a periodic, low-pressure check-in keeps this warm.' },
}

function startOfToday() {
  const d = new Date(); d.setHours(0, 0, 0, 0); return d
}
function hoursSince(iso) {
  if (!iso) return 0
  const t = new Date(iso).getTime()
  if (isNaN(t)) return 0
  return Math.max(0, (Date.now() - t) / 36e5)
}
function daysSince(iso) {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (isNaN(t)) return null
  return Math.floor((Date.now() - t) / 864e5)
}
function relDay(date) {
  const today = startOfToday()
  const d = new Date(date); d.setHours(0, 0, 0, 0)
  const diff = Math.round((d - today) / 864e5)
  if (diff === 0) return 'today'
  if (diff === 1) return 'tomorrow'
  if (diff > 1) return `in ${diff} days`
  if (diff === -1) return 'yesterday'
  return `${Math.abs(diff)} days ago`
}

// Axis 1 dampener — never skips a step, only softens copy and downgrades red -> amber.
function dampenForRelationship(step, judgment) {
  if (!judgment || !judgment.relationship_sensitive) return step
  const softened = RELATIONSHIP_SOFTEN[step.step]
  return {
    ...step,
    urgency: step.urgency === 'red' ? 'amber' : step.urgency,
    title: softened ? softened.title : step.title,
    desc: softened
      ? (judgment.relationship_note ? `${softened.desc} (${judgment.relationship_note})` : softened.desc)
      : step.desc,
  }
}

// Applies axis 1 (dampen) then axis 3 (fit annotation, appended last so it's never silently
// dropped by the dampener's title/desc override) to a rule-chain result. Every return point in
// the normal rule chain goes through this; converted/disqualified/not-a-client bypass it
// entirely (the judgment layer doesn't apply once a lead is closed out or ruled not-a-client).
function finalize(step, judgment) {
  step = dampenForRelationship(step, judgment)
  if (judgment && LOW_FIT.has(judgment.fit_rating) && step.step === 'decision') {
    step = { ...step, desc: `${step.desc} Fit assessment flags this as ${judgment.fit_rating.toLowerCase()} — factor that in.` }
  }
  return step
}

// A 'No' on one of these maps to a suggested close reason (services/leads.js#CLOSE_REASONS keys).
// `decision_ready` is deliberately absent — "I want to explore the possibilities" is the Design
// Consultation track, not a disqualifier (see services/leadQualification.js#CALL_GUIDE).
const NO_ANSWER_CLOSE_REASON = {
  serious: 'not_interested',
  resourced: 'budget',
  project_fit: 'not_a_fit',
  client_fit: 'not_a_fit',
}

/**
 * Rolls the five recorded checklist answers up into a decision recommendation:
 *  { recommend: 'qualify'|'close'|null, suggestReason?, failedLabel?, dcTrack?, unanswered }
 * 'close' on any hard No; 'qualify' when the four decisive questions are all Yes (decision_ready
 * only picks the track); null (neutral) otherwise.
 */
function qualificationRecommendation(qualification) {
  const keys = QUALIFICATION_QUESTIONS.map(q => q.key)
  const q = qualification || {}
  const unanswered = keys.filter(k => !q[k]).length
  const hardNoKey = keys.find(k => q[k] === 'no' && NO_ANSWER_CLOSE_REASON[k])
  if (hardNoKey) {
    const question = QUALIFICATION_QUESTIONS.find(x => x.key === hardNoKey)
    return { recommend: 'close', suggestReason: NO_ANSWER_CLOSE_REASON[hardNoKey], failedLabel: question ? question.label : hardNoKey, unanswered }
  }
  const decisive = keys.filter(k => k !== 'decision_ready')
  if (decisive.every(k => q[k] === 'yes')) {
    return { recommend: 'qualify', dcTrack: q.decision_ready === 'no', unanswered }
  }
  return { recommend: null, unanswered }
}

export function leadNextStep(lead, interactions, judgment, qualification) {
  const ix = Array.isArray(interactions) ? interactions : []

  if (lead.converted) {
    return { step: 'converted', title: 'Lead converted', desc: 'This lead has been converted to an opportunity.', urgency: 'teal', action: null }
  }
  if (lead.status === 'Disqualified') {
    return {
      step: 'disqualified', title: 'Lead disqualified',
      desc: lead.disqualReason ? `Reason: ${lead.disqualReason}.` : 'This lead is no longer being pursued.',
      urgency: 'gray', action: 'requalify',
    }
  }

  // Axis 4 (type mismatch) — bypasses acknowledge/intake/qualify/decision/cold-fallback entirely.
  // An open not-a-client lead is NOT closed yet: the pending action is to close it out (which is
  // where "add them to Contacts" and the other dispositions live) — amber, not gray, and the
  // primary action routes into the unified CloseOutBox with the reason preselected.
  if (judgment && judgment.not_a_client_type) {
    return {
      step: 'not-a-client',
      title: `Not a prospective client (${NOT_CLIENT_LABELS[judgment.not_a_client_type] || judgment.not_a_client_type})`,
      desc: (judgment.not_a_client_note ? `${judgment.not_a_client_note} ` : '')
        + 'Close out the lead — you can add them to the Contacts directory as part of closing.',
      urgency: 'amber', action: 'closeout', suggestReason: 'not_a_client',
    }
  }

  const human = ix.filter(i => AUTO_TYPES.indexOf(i.leadActivityType) === -1)
  // Axis 5 (reactivation) — after a fresh reactivation, pre-reactivation touches no longer count
  // toward "we already had the qualification conversation," so a freshly-reopened record gets a
  // genuinely fresh evaluation instead of resuming stale context.
  const human2 = (judgment && judgment.needs_fresh_evaluation && judgment.reactivated_at)
    ? human.filter(i => i.date && new Date(i.date) >= new Date(judgment.reactivated_at))
    : human

  const days = (lead.daysSinceContact === undefined || lead.daysSinceContact === null) ? null : Number(lead.daysSinceContact)

  // A planned follow-up = the most recent interaction carrying a Next Interaction Date.
  const planned = ix.find(i => i.nextDate)
  const plannedDate = planned ? new Date(planned.nextDate) : null
  const today = startOfToday()
  const plannedFuture = plannedDate ? (plannedDate >= today) : false

  const relCadence = (judgment && judgment.relationship_sensitive) ? (judgment.relationship_cadence_days || 30) : null

  // 1. Acknowledge — no human touch yet.
  if (human2.length === 0) {
    const hrs = hoursSince(lead.createdTime)
    const overdue = hrs > 4
    return finalize({
      step: 'acknowledge',
      title: overdue ? 'Overdue: send initial acknowledgment' : 'Send initial acknowledgment',
      desc: overdue
        ? `This lead has been waiting ${Math.round(hrs)} hours. A personal acknowledgment should go out within 4 business hours.`
        : 'Send a warm, specific acknowledgment within 4 business hours. Reference their inquiry so they feel heard.',
      urgency: overdue ? 'red' : 'amber',
      action: 'log', prefill: { leadActivityType: 'Follow-Up Attempt', type: 'Email' },
    }, judgment)
  }

  // Axis 2 (slow burn) — intercepts before the intake gate and the qualification-conversation
  // gate; rule 1 above still applies (a slow-burn lead needs its real first response), but from
  // here on it runs its own long-horizon cadence instead of the standard funnel.
  if (judgment && judgment.slow_burn && lead.status !== 'Qualified') {
    const staleDays = judgment.slow_burn_stale_after_days || 540
    if (days !== null && days > staleDays) {
      return finalize({
        step: 'slow-burn-stale', title: `No contact in ${Math.round(staleDays / 30)}+ months`,
        desc: 'This long-horizon lead has gone genuinely quiet. Consider a light re-engagement touch, or disqualify with a reason reflecting "timeline never materialized."',
        urgency: 'amber', action: 'decision',
      }, judgment)
    }
    const nextOn = judgment.slow_burn_next_checkin_on ? new Date(judgment.slow_burn_next_checkin_on) : null
    const overdue = nextOn ? nextOn < today : false
    return finalize({
      step: 'slow-burn', title: overdue ? 'Slow-burn check-in due' : 'Long-horizon lead — no active pressure',
      desc: nextOn ? `Next low-pressure check-in ${relDay(nextOn)}.` : 'No active timeline — schedule an occasional check-in (6-12 months out), not a sales cadence.',
      urgency: overdue ? 'amber' : 'teal', action: 'log', prefill: { leadActivityType: 'Follow-Up Attempt', type: 'Email' },
    }, judgment)
  }

  // A real qualifying conversation requires either an explicit "Engaged with Lead"/"Meeting
  // Scheduled" activity type, or at least one touch through a genuinely conversational channel
  // (a live phone/video/in-person exchange, not just any two non-auto-logged rows). The old
  // "human2.length >= 2" fallback let two administrative touches — e.g. an internal skip-note
  // plus an inconclusive call with an intermediary who hadn't even connected the lead yet —
  // falsely satisfy this. A manually-confirmed qualification checklist (below) always overrides,
  // since only a person can actually judge whether the conversation was substantive.
  const CONVERSATIONAL_TYPES = ['Phone Call', 'Video Conference', 'Video Call', 'In Person']
  const engagement = human2.filter(i => ['Engaged with Lead', 'Meeting/Call Scheduled'].indexOf(i.leadActivityType) !== -1)
  const hadConversationalTouch = engagement.length >= 1 || human2.some(i => CONVERSATIONAL_TYPES.indexOf(i.type) !== -1)
  const hasQualConvo = (qualification && qualification.conversation_confirmed) || hadConversationalTouch

  // 3. Qualification conversation — happens BEFORE intake. Qualification is a relationship/fit
  // judgment answerable from one real conversation (docs/CRM.md 2026-07 "Conversion timing"
  // entry); it should never wait on, or be blocked by, a data-gathering step.
  if (!hasQualConvo && lead.status !== 'Qualified') {
    if (plannedFuture) {
      return finalize({ step: 'qualify-call', title: `Qualification call planned ${relDay(plannedDate)}`, desc: 'Confirm scope, site, timeline, and budget fit on the call.', urgency: 'amber', action: 'log', prefill: { leadActivityType: 'Meeting/Call Scheduled' } }, judgment)
    }
    return finalize({ step: 'qualify-call', title: 'Schedule a qualification conversation', desc: 'Confirm scope, site, timeline, and budget fit before deciding.', urgency: (days !== null && days > (relCadence || 7)) ? 'red' : 'amber', action: 'log', prefill: { leadActivityType: 'Meeting/Call Scheduled' } }, judgment)
  }

  // 4. Decision point — driven by the qualification checklist, not just "a conversation
  // happened." The five recorded answers roll up into a recommendation so the card's primary
  // action matches what the checklist already says, instead of leaving the instrument
  // disconnected from the qualify/close buttons (the pre-2026-08 state).
  if (lead.status === 'Engaged' || (hasQualConvo && lead.status !== 'Qualified')) {
    const lateUrgency = (days !== null && days > (relCadence || 5)) ? 'red' : 'amber'
    const rec = qualificationRecommendation(qualification)
    if (rec.recommend === 'qualify') {
      return finalize({
        step: 'decision', recommend: 'qualify',
        title: 'Qualification checks pass — qualify this lead',
        desc: rec.dcTrack
          ? 'Every check came back positive. They\'re still exploring the vision, so this reads as the Design Consultation track — qualify and convert.'
          : 'Every check came back positive. Qualify and begin conversion.',
        urgency: 'teal', action: 'decision',
      }, judgment)
    }
    if (rec.recommend === 'close') {
      return finalize({
        step: 'decision', recommend: 'close', suggestReason: rec.suggestReason,
        title: 'Qualification suggests closing this out',
        desc: `"${rec.failedLabel}" came back No. Recommend closing the lead out — the close reason is preselected; adjust it if the checklist answer doesn't tell the whole story.`,
        urgency: lateUrgency, action: 'decision',
      }, judgment)
    }
    return finalize({
      step: 'decision',
      title: 'Make a qualification decision',
      desc: 'The qualification conversation has happened. Is this lead a fit?'
        + (rec.unanswered > 0 ? ` ${rec.unanswered} of ${QUALIFICATION_QUESTIONS.length} checklist questions still unanswered — answering them sharpens this recommendation.` : ''),
      urgency: lateUrgency, action: 'decision',
    }, judgment)
  }

  // 5. Ready to convert — reached as soon as the lead is Qualified. No data-gathering gate here —
  // the Lead stage's only job is deciding whether this is worth pursuing; a formal project brief
  // is an Opportunity-stage concern (see file header).
  if (lead.status === 'Qualified') {
    return finalize({ step: 'ready', title: 'Ready to convert', desc: 'Brief the team, then convert this lead into an opportunity.', urgency: 'teal', action: 'convert' }, judgment)
  }

  // 6. Cold fallback.
  if (days !== null && days > (relCadence || 14)) {
    return finalize({ step: 'overdue', title: `No activity in ${days} days`, desc: 'This lead has gone cold. Re-engage or disqualify.', urgency: 'red', action: 'decision' }, judgment)
  }

  // 7. Keep momentum.
  return finalize({ step: 'momentum', title: 'Keep the momentum going', desc: days !== null ? `Last contact ${days} day${days === 1 ? '' : 's'} ago. Log the next touch.` : 'Log the next touch to keep this moving.', urgency: 'amber', action: 'log', prefill: { leadActivityType: 'Follow-Up Attempt' } }, judgment)
}

export const URGENCY = {
  red: { bg: 'rgba(179,38,30,0.06)', border: 'rgba(179,38,30,0.3)', accent: '#b3261e', label: 'Overdue' },
  amber: { bg: 'rgba(193,122,0,0.07)', border: 'rgba(193,122,0,0.3)', accent: '#9a6700', label: 'Next step' },
  teal: { bg: 'rgba(29,158,117,0.08)', border: 'rgba(29,158,117,0.3)', accent: '#1f9d57', label: 'Ready' },
  gray: { bg: 'rgba(7,39,45,0.04)', border: 'rgba(7,39,45,0.15)', accent: '#5a7884', label: 'Closed' },
}
